// galéria oldal / főoldal

import React, { useCallback, useEffect, useState} from 'react';
import { useNavigate} from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useDoubleConfirm } from '../contexts/AlertContext';
 //képek
const DEFAULT_HERO_TITLE = 'Prémium Hajvágás';
const DEFAULT_HERO_DESCRIPTION = 'Tapasztald meg a minőségi fodrászatot. Precíz vágások, modern stílusok és klasszikus elegancia egy helyen.';
const MAX_GALLERY_UPLOAD_SIZE = 800 * 1024;

function ImageWithFallback({ src, alt, className }) {
  const [failedSrc, setFailedSrc] = useState(null);

  if (failedSrc === src) {
    return (
      <div className="flex h-full w-full items-center justify-center p-2 text-center text-sm text-slate-600 dark:text-slate-300">
        Hiba a kép betöltése során
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} onError={() => setFailedSrc(src)} />;
}

const compressGalleryImage = async (file) => {
  if (file.size <= MAX_GALLERY_UPLOAD_SIZE) return file;
  if (file.type === 'image/gif') {
    throw new Error('A GIF túl nagy az automatikus tömörítéshez. Kérjük, válassz egy kisebb képet!');
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.75));
  if (!blob || blob.type !== 'image/webp' || blob.size > MAX_GALLERY_UPLOAD_SIZE) {
    throw new Error('A kép tömörítés után is túl nagy. Kérjük, válassz egy kisebb képet!');
  }

  const filename = file.name.replace(/\.[^.]+$/, '') || 'gallery-image';
  return new File([blob], `${filename}.webp`, { type: 'image/webp', lastModified: Date.now() });
};

export default function HomePage() {
  const { user } = useAuth();
  const showAlert = useAlert();
  const doubleConfirm = useDoubleConfirm();
  const [galleryItems, setGalleryItems] = useState([]);
  const [heroTitle, setHeroTitle] = useState(DEFAULT_HERO_TITLE);
  const [heroDescription, setHeroDescription] = useState(DEFAULT_HERO_DESCRIPTION);
  const [heroContentStatus, setHeroContentStatus] = useState('loading');
  const [heroEditorOpen, setHeroEditorOpen] = useState(false);
  const [heroTitleDraft, setHeroTitleDraft] = useState(DEFAULT_HERO_TITLE);
  const [heroDescriptionDraft, setHeroDescriptionDraft] = useState(DEFAULT_HERO_DESCRIPTION);
  const navigate = useNavigate();

  // Szerkesztési és űrlap állapotok
  const [editingId, setEditingId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Kép nagyítása
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  // Galéria elemek lekérése, sikeres válasznál frissíti a megjelenített listát.
  const fetchGallery = useCallback(() => {
    axios.get(`/api/gallery`)
      .then(res => setGalleryItems(res.data))
      .catch(err => console.error("Hiba a galéria lekérésekor:", err));
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  useEffect(() => {
    axios.get('/api/settings')
      .then((res) => {
        setHeroTitle(res.data.homepage_hero_title || DEFAULT_HERO_TITLE);
        setHeroDescription(res.data.homepage_hero_description ?? DEFAULT_HERO_DESCRIPTION);
        setHeroContentStatus('loaded');
      })
      .catch((err) => {
        console.error('Hiba a nyitó szövegek lekérésekor:', err);
        setHeroContentStatus('error');
      });
  }, []);

  // A nyitó szakasz gombja az időpontfoglalási oldalra navigál.
  const handleBookClick = () => {
    navigate('/foglalas');
  };

  const handleHeroContentSubmit = async (e) => {
    e.preventDefault();
    const title = heroTitleDraft.trim();
    const description = heroDescriptionDraft.trim();

    if (!title || title.length > 100 || description.length > 255) {
      showAlert('A főcím 1-100, a leírás legfeljebb 255 karakter lehet!');
      return;
    }

    try {
      await axios.put('/api/homepage-content', {
        userId: user.id,
        title,
        description
      });
      setHeroTitle(title);
      setHeroDescription(description);
      setHeroEditorOpen(false);
      showAlert('A nyitó szövegek mentése sikerült.');
    } catch (err) {
      showAlert(err.response?.data?.error || 'Hiba a nyitó szövegek mentésekor!');
    }
  };

  // A feltöltött fájlt eltárolja, és helyi előnézeti képet készít belőle.
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const uploadFile = await compressGalleryImage(file);
      setSelectedFile(uploadFile);
      setPreviewUrl(URL.createObjectURL(uploadFile));
    } catch (error) {
      setSelectedFile(null);
      showAlert(error.message || 'A kép feldolgozása sikertelen!');
      e.target.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    const description = newDesc.trim();

    // A cím és a leírás hosszát, valamint a feltöltéshez szükséges adatokat ellenőrzi.
    if (title.length < 3 || title.length > 50) {
      showAlert("A cím 3-50 karakter hosszú legyen!");
      return;
    }

    if (description.length > 160) {
      showAlert("A leírás legfeljebb 160 karakter hosszú lehet!");
      return;
    }

    if (!editingId && !selectedFile) {
      showAlert("Kérjük, tölts fel egy képet is!");
      return;
    }

    if (!editingId && galleryItems.length >= 10) {
      showAlert("⚠️ Figyelem! Elérted a maximális 10 darabos limitet a galériában.\n\nHa új képet szeretnél hozzáadni, előbb törölnöd kell egy meglévő elemet a listából!");
      return;
    }

    const payload = new FormData();
    payload.append('title', title);
    payload.append('description', description);
    if (selectedFile) payload.append('image', selectedFile);
    if (!selectedFile && editingId) payload.append('url', previewUrl || '/barber.jpg');

    // Szerkesztéskor frissít, új elemnél pedig létrehoz egy galériaelemet.
    if (editingId) {
      axios.put(`/api/gallery/${editingId}`, payload)
        .then(() => {
          fetchGallery();
          resetForm();
        })
        .catch(err => {
          showAlert(err.response?.status === 413
            ? 'A szerver túl nagynak találta a feltöltést. Próbálj meg egy kisebb képet!'
            : err.response?.data?.error || "Hiba a módosítás során!");
        });
    } else {
      axios.post(`/api/gallery`, payload)
        .then(() => {
          fetchGallery();
          resetForm();
        })
        .catch(err => {
          if (err.response?.status === 413) {
            showAlert('A szerver túl nagynak találta a feltöltést. Próbálj meg egy kisebb képet!');
            return;
          }
          if (err.response && err.response.data && err.response.data.error) {
            showAlert(err.response.data.error);
          } else {
            console.error("Hiba a hozzáadás során:", err);
          }
        });
    }
  };

  // Betölti a kiválasztott elem adatait az űrlapba, majd az űrlaphoz görget.
  const handleEditClick = (item) => {
    setEditingId(item.id);
    setNewTitle(item.title);
    setNewDesc(item.description);
    setSelectedFile(null);
    setPreviewUrl(item.url);
    
    document.getElementById('gallery-editor-pane')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Visszaállítja az űrlapot új elem felvételére alkalmas állapotba.
  const resetForm = () => {
    setEditingId(null);
    setNewTitle('');
    setNewDesc('');
    setSelectedFile(null);
    setPreviewUrl('');
  };

  // A szerver relatív képelérési útvonalát teljes URL-lé alakítja.
  const getImageUrl = (url) => url?.startsWith('/pictures/')
    ? new URL(url, axios.defaults.baseURL || window.location.origin).toString()
    : url;

    //kép törlése metodus
  const handleDeleteItem = async (id) => {
    // Törlés csak a kétlépcsős megerősítés után indulhat el.
    if (await doubleConfirm("Biztosan törölni szeretnéd ezt a galéria elemet az adatbázisból?", "A galériaelem véglegesen törlődik. Biztosan folytatod?")) {
      axios.delete(`/api/gallery/${id}`)
        .then(() => {
          fetchGallery();
          if (editingId === id) resetForm();
        })
        .catch(err => console.error("Hiba a törlés során:", err));
    }
  };

  // Átkapcsolja az adott kép kijelölési állapotát.
  const toggleImageSelection = (id) => {
    setSelectedImageIds((currentIds) => (
      currentIds.includes(id)
        ? currentIds.filter((selectedId) => selectedId !== id)
        : [...currentIds, id]
    ));
  };

  // Megerősítés után párhuzamosan törli a kijelölt elemeket.
  const handleDeleteSelected = async () => {
    if (selectedImageIds.length === 0) return;

    const confirmed = await doubleConfirm(
      `Biztosan törölni szeretnéd a kijelölt ${selectedImageIds.length} képet?`,
      'A kijelölt képek véglegesen törlődnek. Biztosan folytatod?'
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        selectedImageIds.map((id) => axios.delete(`/api/gallery/${id}`))
      );
      if (editingId && selectedImageIds.includes(editingId)) resetForm();
      setSelectedImageIds([]);
      fetchGallery();
    } catch (error) {
      showAlert(error.response?.data?.error || 'Hiba történt a kijelölt képek törlésekor!');
    }
  };

  return (
    <div className="w-full">
      
      {/* Nyitó szakasz: bemutatkozó szöveg és időpontfoglalás. */}
      <div className="relative text-white py-16 px-6 overflow-hidden mb-12 mx-4 lg:mx-auto max-w-7xl mt-4">
         <div className="relative z-10 text-center max-w-4xl mx-auto">
          {heroContentStatus === 'loading' ? (
            <p className="mb-10 text-xl text-slate-700 dark:text-slate-300">Betöltés</p>
          ) : heroContentStatus === 'error' ? (
            <p className="mb-10 text-xl text-slate-700 dark:text-slate-300">Hiba a betöltés közben</p>
          ) : (
            <>
              <h1 className="text-5xl lg:text-7xl font-black text-transparent drop-shadow-[0_1.2px_1.2px_rgba(255,255,255,0.8)] dark:drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] bg-gradient-to-br bg-clip-text from-fuchsia-800 to-fuchsia-900 dark:from-fuchsia-400 dark:to-fuchsia-800 mb-6 pb-[20px]">
                {heroTitle}
              </h1>
              <p className="text-xl text-slate-700 dark:text-slate-300 mb-10 leading-relaxed">
                {heroDescription}
              </p>
            </>
          )}
            {heroContentStatus === 'loaded' && user?.role === 'Fodrász' && (
              <button
                type="button"
                onClick={() => {
                  setHeroTitleDraft(heroTitle);
                  setHeroDescriptionDraft(heroDescription);
                  setHeroEditorOpen(true);
                }}
                className="mx-auto mb-4 block rounded-[1rem] border border-purple-950 bg-white/80 px-4 py-2 font-bold text-purple-950 transition-colors hover:bg-purple-950 hover:text-white dark:border-fuchsia-400 dark:bg-slate-900 dark:text-fuchsia-300 dark:hover:bg-fuchsia-400 dark:hover:text-black"
              >
                Nyitó szövegek szerkesztése
              </button>
            )}
            {/* Félátlátszó hátterű, elmosott gomb */}
            <button 
              id="foglal"
              onClick={handleBookClick}
              className="bg-white/40 dark:bg-black/50 backdrop-blur-sm hover:bg-purple-950 dark:hover:bg-[#a855f7] text-purple-900 dark:text-[#a855f7] hover:text-white dark:hover:text-black border-2 border-purple-950 dark:border-[#a855f7] px-10 py-5 rounded-[1.5rem] text-xl font-bold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(168,85,247,0.1)]"
            >
              Időpontot foglalok
            </button>
         </div>
      </div>

      {heroEditorOpen && user?.role === 'Fodrász' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setHeroEditorOpen(false)}
        >
          <form
            onSubmit={handleHeroContentSubmit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hero-editor-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl space-y-5 rounded-xl border border-slate-200 bg-white p-6 text-left shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-center justify-between gap-4">
              <h2 id="hero-editor-title" className="text-xl font-bold text-slate-900 dark:text-white">
                Nyitó szövegek szerkesztése
              </h2>
              <button
                type="button"
                onClick={() => setHeroEditorOpen(false)}
                aria-label="Bezárás"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                ×
              </button>
            </div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              Főcím
              <input
                type="text"
                maxLength={100}
                value={heroTitleDraft}
                onChange={(e) => setHeroTitleDraft(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-fuchsia-600 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </label>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              Leírás
              <textarea
                maxLength={255}
                rows={4}
                value={heroDescriptionDraft}
                onChange={(e) => setHeroDescriptionDraft(e.target.value)}
                className="mt-1 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-fuchsia-600 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </label>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setHeroEditorOpen(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Mégse
              </button>
              <button
                type="submit"
                className="rounded-lg bg-fuchsia-700 px-4 py-2 font-bold text-white hover:bg-fuchsia-600"
              >
                Mentés
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Galéria: fodrászoknak szerkesztőfelület, minden látogatónak képlista. */}
      <div className="max-w-7xl mx-auto p-8 mb-12 bg-white/90 dark:bg-slate-900/90 rounded-xl border-2 border-purple-950 dark:border-purple-500/100 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:shadow-2xl text-slate-800 dark:text-white transition-colors">
        <div className="text-center mb-10 border-b border-slate-200 dark:border-purple-500/20 pb-6">
          <h2 className="text-4xl font-black bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent mb-2">
            Galéria
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Tekintsd meg kiemelt munkáinkat!
          </p>
          {user?.role === 'Fodrász' && selectedImageIds.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <span className="rounded-xl bg-purple-100 dark:bg-purple-500/15 px-3 py-1 text-xs font-bold text-purple-700 dark:text-pink-300">
                {selectedImageIds.length} kép kijelölve
              </span>
              <button
                type="button"
                onClick={handleDeleteSelected}
                className="rounded-xl bg-rose-600 px-4 py-2 text-xs font-black uppercase text-white shadow-lg transition-colors hover:bg-rose-500"
              >
                Kijelöltek törlése
              </button>
              <button
                type="button"
                onClick={() => setSelectedImageIds([])}
                className="rounded-xl bg-slate-200 dark:bg-slate-800 px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-300 dark:hover:bg-slate-700"
              >
                Kijelölés törlése
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          
          {/* Szerkesztőpanel (fodrásznak). */}
          {user?.role === 'Fodrász' && (
            <div id="gallery-editor-pane" className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-dashed border-purple-300 dark:border-purple-500/50 shadow-xl lg:col-span-1 sticky top-24 transition-colors">
              <div className="flex justify-between items-center mb-2 border-b border-slate-200 dark:border-purple-500/20 pb-2">
                <h3 className="text-lg font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                  {editingId ? 'Szerkesztés' : 'Új kép'}
                </h3>
                {editingId && (
                  <button onClick={resetForm} className="text-xs bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300 transition-colors">
                    Mégse
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 mb-4">
              <div className="text-xs text-left text-slate-500 dark:text-slate-400 font-semibold">
                <span>Maximális fájlméret: <span className="font-extrabold">20MB</span> </span>
              </div>
              <div className="text-xs text-right text-slate-500 dark:text-slate-400 font-semibold">
                Képek száma: <span className={galleryItems.length >= 10 ? "text-rose-500 dark:text-rose-400 font-bold" : "text-emerald-600 dark:text-emerald-400"}>{galleryItems.length}</span> / 10
              </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-col sm:flex-row lg:flex-col items-center gap-6">
                  
                  {/* Képfeltöltő mező az aktuális kép előnézetével. */}
                  <label className="w-32 h-32 min-w-[128px] bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border-2 border-purple-300 dark:border-purple-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-all group relative">
                    {previewUrl ? (
                      <ImageWithFallback src={getImageUrl(previewUrl)} alt="Előnézet" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-2">
                        <span className="text-5xl font-light text-slate-400 group-hover:text-purple-500 dark:group-hover:text-purple-400 transition-colors block leading-none mb-1">+</span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest block">Kép helye</span>
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageChange} 
                      className="hidden" 
                    />
                  </label>

                  {/* A galériaelem címe és leírása. */}
                  <div className="flex-grow w-full space-y-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 font-bold">Cím</label>
                      <input 
                        type="text"
                        maxLength={50}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Pl. Modern Fade vágás"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-purple-200 dark:border-purple-500/30 rounded-xl px-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 font-bold">Leírás</label>
                      <input 
                        type="text"
                        maxLength={160}
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="Pl. Precíziós átmenet borotvával"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-purple-200 dark:border-purple-500/30 rounded-xl px-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className={`w-full text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg active:scale-[0.99] ${
                    editingId 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' 
                      : galleryItems.length >= 10
                        ? 'bg-slate-700 cursor-not-allowed opacity-50'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-purple-600/20'
                  }`}
                >
                  {editingId ? 'Módosítások mentése' : galleryItems.length >= 10 ? 'Limit elérve (Törölj egyet)' : 'Hozzáadás a galériához'}
                </button>
              </form>
            </div>
          )}

          {/* Galériaelemek listája, üres állapotjelzéssel. */}
          <div className={`space-y-4 ${user?.role === 'Fodrász' ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            {galleryItems.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 italic py-8 text-center bg-slate-100 dark:bg-slate-950/40 rounded-2xl border border-slate-200 dark:border-purple-500/10">
                Még nincs megjeleníthető kép a galériában.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {galleryItems.map((img) => (
                  <div 
                    key={img.id} 
                    className={`flex bg-white/95 dark:bg-slate-950/80 rounded-xl p-4 border transition-all duration-300 backdrop-blur-xl relative group shadow-lg dark:shadow-xl ${
                      selectedImageIds.includes(img.id)
                        ? 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-500/50 bg-purple-50 dark:bg-purple-950/40 shadow-purple-200 dark:shadow-purple-500/10'
                        : editingId === img.id 
                        ? 'border-amber-400 bg-amber-50 dark:bg-slate-900' 
                        : 'border-slate-200 dark:border-purple-500/20 hover:border-purple-300 dark:hover:border-purple-500/50 hover:bg-slate-50 dark:hover:bg-slate-900/80'
                    } ${user?.role === 'Fodrász' ? 'cursor-pointer' : ''}`}
                    onClick={user?.role === 'Fodrász' ? () => handleEditClick(img) : undefined}
                  >
                    <div 
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedImage(img);
                      }}
                      className="w-1/3 aspect-square min-w-[90px] max-w-[110px] rounded-xl overflow-hidden bg-slate-200 dark:bg-black border border-slate-200 dark:border-purple-500/30 flex-shrink-0 cursor-zoom-in relative group/img"
                    >
                      <ImageWithFallback 
                        src={getImageUrl(img.url)} 
                        alt={img.title} 
                        className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity text-white text-xs font-bold">
                        Nagyítás
                      </div>
                    </div>

                    <div className="w-[1px] bg-slate-200 dark:bg-purple-500/20 mx-4 my-1 self-stretch"></div>

                    <div className="flex flex-col justify-center flex-grow min-w-0 pr-16">
                      <h4 className="text-lg font-bold text-purple-600 dark:text-purple-300 mb-1 truncate">
                        {img.title}
                      </h4>
                      <p className="text-slate-600 dark:text-slate-300 text-sm leading-snug break-words">
                        {img.description}
                      </p>
                    </div>

                    {user?.role === 'Fodrász' && (
                      <div
                        className="absolute top-3 right-3 flex flex-col items-end gap-2 opacity-80 group-hover:opacity-100 transition-opacity"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditClick(img)}
                            className="h-9 w-9 inline-flex items-center justify-center bg-purple-100 dark:bg-purple-500/20 hover:bg-purple-600 text-purple-600 dark:text-purple-300 hover:text-white rounded-xl transition-all duration-200 shadow-md"
                            title="Elem módosítása"
                          >
                          <span className="text-lg leading-none" aria-hidden="true">✎</span>
                          <svg className="hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          </button>

                        {/*kép törlése gomb*/ }
                          <button
                            onClick={() => handleDeleteItem(img.id)}
                            className="h-9 w-9 inline-flex items-center justify-center bg-rose-100 dark:bg-rose-500/20 hover:bg-rose-600 text-rose-600 dark:text-rose-300 hover:text-white rounded-xl transition-all duration-200 shadow-md"
                            title="Elem törlése"
                          >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          </button>
                        </div>

                        <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-0.5 py-1.5 text-[10px] font-bold shadow-md transition-colors ${
                          selectedImageIds.includes(img.id)
                            ? 'border-purple-600 bg-purple-600 text-white'
                            : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:border-purple-400 hover:bg-purple-600 hover:text-white'
                        }`}>
                            <input
                              type="checkbox"
                              checked={selectedImageIds.includes(img.id)}
                              onChange={() => toggleImageSelection(img.id)}
                              className="h-4 w-4 accent-purple-500"
                              aria-label={`${img.title} kijelölése`}
                            />
                            <span>Kijelölés</span>
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Nagyított kép: a háttérre vagy a bezáró gombra kattintva zárható be. */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 backdrop-blur-md transition-all"
          onClick={() => setSelectedImage(null)}
        >
           

          <div 
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-purple-500/30 rounded-3xl p-4 max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl relative text-slate-800 dark:text-white"
            onClick={(e) => e.stopPropagation()}
          >
           
             <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-6  right-6 right bg-slate-200/50 dark:bg-slate-800/70 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-white font-bold h-10 w-10 flex items-center justify-center rounded-full transition-colors border border-purple-400 dark:border-purple-500/60 text-xl z-10 shadow-lg"
            >
            ✕
            </button>

            <div className="w-full flex-grow overflow-hidden rounded-2xl bg-slate-100 dark:bg-black flex items-center justify-center min-h-[250px]">
              <ImageWithFallback 
                src={getImageUrl(selectedImage.url)} 
                alt={selectedImage.title} 
                className="max-w-full max-h-[65vh] object-contain"
              />
             
            </div>

            <div className="mt-4 border-t border-slate-200 dark:border-purple-500/20 pt-4 px-2">
              <h3 className="text-2xl font-black text-purple-600 dark:text-purple-300 mb-1">{selectedImage.title}</h3>
              <p className="text-slate-600 dark:text-slate-300 text-base">{selectedImage.description}</p>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}